import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import CKEditor from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// Material-UI Core
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import Fab from "@material-ui/core/Fab";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";

// Material-UI Icon
import AddIcon from "@material-ui/icons/Add";
import Button from "@material-ui/core/Button";

// Custom Component
import useLoading from "../Loading/useLoading";
import Legend from "../Legend/Legend";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import SaveWarnDialog from "../Dialog/SaveWarnDialog";
import FullScreenDialog from "../Dialog/FullScreenDialog";
import NormalDialog from "../Dialog/NormalDialog";
import RecipientChip from "../Chip/RecipientChip";
import CirculationRecipientForm from "./CirculationRecipientForm";
import FileDropzone from "../FileDropzone/FileDropzone";
import CirculationFileZoneContent from "./CirculationFileZoneContent";
import FilePreview from "../FilePreview/FilePreview";
import LockScreenLoading from "../LockScreenLoading/LockScreenLoading";

// Custom Style
import { redColor, grayColor } from "../../styles/jss/cmsMainStyle";
import { circulationForwardFormStyle } from "../../styles/jss/components/circulation/circulationForwardFormStyle";

// Global Function
import { ckEditorToolbar } from "../../global/ckEditor";
import { webViewerSaveAnnotation } from "../../global/webViewerSaveAnnotation";

// Fetch Data
import {
  getUser,
  getUserGroupType,
  getUserGroup,
  createCirculation,
  getForwardData,
} from "../../dataSource/circulation";
import { fileUpload } from "../../dataSource/fileManager";

const useStyles = makeStyles((theme) => circulationForwardFormStyle(theme));
const store = require("store");

const CirculationCreateForm = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    presetSubject,
    presetFiles,
    setResetFileSelect,
    openFileManagerDialog,
    circulationSeq,
    closeCirculationDialog,
    renewCircList,
    setRenewCircList,
    buildAlert,
  } = props;

  const circulationDataTemplate = {
    circ_subject: "",
    forward_from_circ_seq: "",

    recipients: {
      CC: [],
      TO: [],
    },

    messages: {
      parent_seq: 0,
      content: "",
      dms_property_seqs: [],
      files: [],
    },
  };

  // Redux Data
  const userListRedux = useSelector((state) => state.userListRedux);
  const userGroupListRedux = useSelector((state) => state.userGroupListRedux);
  const circulationDataRedux = useSelector(
    (state) => state.circulationDataRedux
  );
  const propertySeqRedux = useSelector((state) => state.propertySeqRedux);
  const userModulesRedux = useSelector((state) => state.userModulesRedux);
  const [lockScreenLoadingOpen, setLockScreenLoadingOpen] = useState(false);
  const [progressTitle, setProgressTitle] = useState("Sending");

  const [getUserListLoading, getUserListShowLoading, getUserListHiddenLoading] =
    useLoading();
  const [
    getGroupListLoading,
    getGroupListShowLoading,
    getGroupListHiddenLoading,
  ] = useLoading();

  const [recipientType, setRecipientType] = useState(null);
  const [userGroupType, setUserGroupType] = useState(null);
  const [userList, setUserList] = useState(null);
  const [externalUserList, setExternalUserList] = useState(null);
  const [userGroupList, setUserGroupList] = useState(null);

  const [leftToUserRcpt, setLeftToUserRcpt] = useState(null);
  const [rightToUserRcpt, setRightToUserRcpt] = useState(null);
  const [leftToGroupRcpt, setLeftToGroupRcpt] = useState(null);
  const [rightToGroupRcpt, setRightToGroupRcpt] = useState(null);
  const [leftCcUserRcpt, setLeftCcUserRcpt] = useState(null);
  const [rightCcUserRcpt, setRightCcUserRcpt] = useState(null);
  const [leftCcGroupRcpt, setLeftCcGroupRcpt] = useState(null);
  const [rightCcGroupRcpt, setRightCcGroupRcpt] = useState(null);
  const [editorValue, setEditorValue] = useState("");
  const [circulationSubject, setCirculationSubject] = useState("");

  const [filePreviewProperty, SetFilePreviewProperty] = useState(null);
  const [filePreviewDialogOpen, setFilePreviewDialogOpen] = useState(false);
  const [selectUserDialogOpen, setSelectUserDialogOpen] = useState(false);
  const [forwardDetail, setForwardDetail] = useState(null);
  const [circulationData, setCirculationData] = useState(
    circulationDataTemplate
  );
  const [validationErrors, setValidationErrors] = useState({});

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState({
    dialogTitle: "",
    dialogContent: "",
    confirmAction: "",
    cancelAction: "",
  });

  const [saveWarnDialogOpen, setSaveWarnDialogOpen] = useState(false);
  const [saveWarnDialogData, setSaveWarnDialogData] = useState({
    dialogTitle: "",
    dialogContent: "",
    saveAction: "",
    dontSaveAction: "",
    cancelAction: "",
  });

  // CKEditor Config
  const editorConfiguration = {
    // plugins: [ Essentials, Bold, Italic, Paragraph ],
    toolbar: ckEditorToolbar,
  };

  // Build Confirm Dialog
  const confirmDialogHandleOpen = (data) => {
    setConfirmDialogData({
      dialogTitle: data.dialogTitle,
      dialogContent: data.dialogContent,
      confirmAction: data.confirmAction,
      cancelAction: data.cancelAction,
    });

    setConfirmDialogOpen(true);
  };

  const confirmDialogHandleClose = () => {
    setConfirmDialogOpen(false);
  };

  // Build Save Warn Dialog
  const saveWarnDialogHandleOpen = (data) => {
    setSaveWarnDialogData({
      dialogTitle: data.dialogTitle,
      dialogContent: data.dialogContent,
      saveAction: data.saveAction,
      dontSaveAction: data.dontSaveAction,
      cancelAction: data.cancelAction,
    });

    setSaveWarnDialogOpen(true);
  };

  const saveWarnDialogHandleClose = () => {
    setSaveWarnDialogOpen(false);
  };

  // Handle Dialog open and close
  const selectRcptDialogHandleOpen = (type) => {
    setRecipientType(type);
    setSelectUserDialogOpen(true);
  };

  const selectRcptDialogHandleClose = () => {
    setRecipientType(null);
    setSelectUserDialogOpen(false);
  };

  const filePreviewDialogHandleOpen = () => {
    setFilePreviewDialogOpen(true);
  };

  const filePreviewDialogHandleClose = () => {
    if (!store.get("annotActive")) {
      setFilePreviewDialogOpen(false);
    } else {
      alertSaveActionHandleOpen();
    }
  };

  // Get Data
  const getUserListAction = () => {
    getUserListShowLoading();

    getUser(true).then((res) => {
      setUserList(res.data);
      dispatch({
        type: "ADD_USER_LIST",
        data: res.data,
      });

      getUser(false).then((res) => {
        setExternalUserList(res.data);
        dispatch({
          type: "ADD_USER_LIST",
          data: res.data,
        });
      });

      getUserListHiddenLoading();
    });
  };

  const getGroupListAction = (groupTypeList) => {
    getGroupListShowLoading();

    let tempLeftGroupList = {};
    let tempRightGroupList = {};

    if (groupTypeList !== null) {
      groupTypeList.map((type) => {
        getUserGroup(type.type_seq).then((res) => {
          tempLeftGroupList[type.type_seq] = res.data;
          tempRightGroupList[type.type_seq] = [];

          setLeftToGroupRcpt(tempLeftGroupList);
          setRightToGroupRcpt(tempRightGroupList);

          setLeftCcGroupRcpt(tempLeftGroupList);
          setRightCcGroupRcpt(tempRightGroupList);

          setUserGroupList(tempLeftGroupList);
          dispatch({
            type: "ADD_USER_GROUP_LIST",
            data: tempLeftGroupList,
          });

          getGroupListHiddenLoading();
        });
      });
    }
  };

  // Handle Form Input
  const handleSubjectChange = (validateOnChange) => (event) => {
    const { value } = event.target;

    setCirculationSubject(value);
    setCirculationData({ ...circulationData, circ_subject: value });

    if (validateOnChange) {
      validate({ ...circulationData, circ_subject: value });
    }
  };

  const alertSaveActionHandleOpen = () => {
    saveWarnDialogHandleOpen({
      dialogTitle: "Annotation Change Warning",
      dialogContent: "Do you want to save the change of annotation.",
      saveAction: alertSaveAnnotation,
      dontSaveAction: alertDontSaveAnnotation,
      cancelAction: alertSaveActionHandleClose,
    });
  };

  const alertSaveActionHandleClose = () => {
    saveWarnDialogHandleClose();
  };

  const alertSaveAnnotation = () => {
    annotationSaveAction();

    alertSaveActionHandleClose();
    setFilePreviewDialogOpen(false);
  };

  const alertDontSaveAnnotation = () => {
    alertSaveActionHandleClose();
    setFilePreviewDialogOpen(false);
  };

  const ckeditorHandleChange = (event, editor) => {
    const inputData = editor.getData();
    const data = { ...circulationData };

    setEditorValue(inputData);
    data.messages.content = inputData;
    setCirculationData({ ...circulationData, ...data });

    if (
      validationErrors.content && validationErrors.content !== "" ? true : false
    ) {
      validate({ ...circulationData, content: inputData });
    }
  };

  const annotationSaveActionHandleOpen = () => {
    confirmDialogHandleOpen({
      dialogTitle: "Annotation Save",
      dialogContent: "Are you sure to save the annotation.",
      confirmAction: annotationSaveAction,
      cancelAction: annotationSaveActionHandleClose,
    });
  };

  const annotationSaveActionHandleClose = () => {
    confirmDialogHandleClose();
  };

  const annotationSaveAction = () => {
    webViewerSaveAnnotation(
      filePreviewProperty.fileCategory,
      filePreviewProperty.fileSequence
    ).then((res) => {
      annotationSaveActionHandleClose();

      if (res.type === "success") {
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "success",
          title: "Success Alert",
          content: "The Annotation have been save",
        });
      } else {
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "error",
          title: "Error Alert",
          content: res.message,
        });
      }
    });
  };

  // Main Action
  const fileUploadAction = (fileType, acceptedFilesPool) => {
    acceptedFilesPool.map((file) => {
      fileUpload(file).then((res) => {
        console.log(
          "*****************************************************************************"
        );
        console.log("Temporary upload File Successful");
        console.log(res);
        console.log(
          "*****************************************************************************"
        );

        buildFileRecord(fileType, res.data[0]);
      });
    });
  };

  const buildFileRecord = (fileType, fileUploadRecord) => {
    const val = { ...circulationData };
    fileUploadRecord.modifydate = "";
    val.messages.files.push(fileUploadRecord);

    setCirculationData({ ...circulationData, ...val });
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: { ...circulationData, val },
    });
  };

  const reciptentChipAdd = (type, recipientRec) => {
    let tempRecipients = {
      TO: [...circulationData.recipients.TO],
      CC: [...circulationData.recipients.CC],
    };

    if (type === "USER") {
      if (recipientRec !== null) {
        recipientRec.map((res) => {
          tempRecipients[recipientType].push({
            rcpt_type: "USER", // USER, USERGROUP
            site_user_seq: res.site_user_seq,
            fullname: res.fullname,
          });
        });
      }
    } else if (type === "GROUP") {
      if (recipientRec !== null) {
        recipientRec.map((res) => {
          tempRecipients[recipientType].push({
            rcpt_type: "USERGROUP", // USER, USERGROUP
            type_seq: res.type_seq,
            group_seq: res.group_seq,
            group_name: res.group_name,
          });
        });
      }
    }

    setCirculationData({ ...circulationData, recipients: tempRecipients });
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: { ...circulationData, recipients: tempRecipients },
    });

    if (validationErrors.TO && validationErrors.TO !== "" ? true : false) {
      validate({ ...circulationData, recipients: tempRecipients });
    }
  };

  const reciptentChipRemove = (type, recipientRec) => {
    let tempRecipients = {
      TO: [...circulationData.recipients.TO],
      CC: [...circulationData.recipients.CC],
    };

    if (type === "USER") {
      if (recipientRec !== null) {
        recipientRec.map((res) => {
          tempRecipients[recipientType].map((data, index) => {
            if (res.site_user_seq === data.site_user_seq) {
              tempRecipients[recipientType].splice(index, 1);
            }
          });
        });
      }
    } else if (type === "GROUP") {
      if (recipientRec !== null) {
        recipientRec.map((res) => {
          tempRecipients[recipientType].map((data, index) => {
            if (res.group_seq === data.group_seq) {
              tempRecipients[recipientType].splice(index, 1);
            }
          });
        });
      }
    }

    setCirculationData({ ...circulationData, recipients: tempRecipients });
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: { ...circulationData, recipients: tempRecipients },
    });
  };

  const removeRecipient = (recipientType, removeRec) => {
    let result = {
      TO: [...circulationData.recipients.TO],
      CC: [...circulationData.recipients.CC],
    };

    result[recipientType] = circulationData.recipients[recipientType].filter(
      (rec) => rec !== removeRec
    );
    setCirculationData({ ...circulationData, recipients: result });
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: { ...circulationData, recipients: result },
    });
  };

  const moveTransferListData = (recipientType, typeSeq, removeRec) => {
    let groupRightResult =
      recipientType === "TO"
        ? { ...rightToGroupRcpt }
        : recipientType === "CC"
        ? { ...rightCcGroupRcpt }
        : [];
    let groupLeftResult =
      recipientType === "TO"
        ? { ...leftToGroupRcpt }
        : recipientType === "CC"
        ? { ...leftCcGroupRcpt }
        : [];

    if (removeRec.rcpt_type === "USER") {
      if (recipientType === "TO") {
        setRightToUserRcpt(
          rightToUserRcpt.filter(
            (rec) => rec.site_user_seq !== removeRec.site_user_seq
          )
        );
        setLeftToUserRcpt([
          ...leftToUserRcpt,
          ...rightToUserRcpt.filter(
            (rec) => rec.site_user_seq === removeRec.site_user_seq
          ),
        ]);
      } else if (recipientType === "CC") {
        setRightCcUserRcpt(
          rightCcUserRcpt.filter(
            (rec) => rec.site_user_seq !== removeRec.site_user_seq
          )
        );
        setLeftCcUserRcpt([
          ...leftCcUserRcpt,
          ...rightCcUserRcpt.filter(
            (rec) => rec.site_user_seq === removeRec.site_user_seq
          ),
        ]);
      }
    } else if (removeRec.rcpt_type === "USERGROUP") {
      if (recipientType === "TO") {
        groupRightResult[typeSeq] = rightToGroupRcpt[typeSeq].filter(
          (rec) => rec.group_seq !== removeRec.group_seq
        );
        groupLeftResult[typeSeq].push(
          ...rightToGroupRcpt[typeSeq].filter(
            (rec) => rec.group_seq === removeRec.group_seq
          )
        );

        setRightToGroupRcpt({ ...rightToGroupRcpt, ...groupRightResult });
        setLeftToGroupRcpt({ ...leftToGroupRcpt, ...groupLeftResult });
      } else if (recipientType === "CC") {
        groupRightResult[typeSeq] = rightCcGroupRcpt[typeSeq].filter(
          (rec) => rec.group_seq !== removeRec.group_seq
        );
        groupLeftResult[typeSeq].push(
          ...rightCcGroupRcpt[typeSeq].filter(
            (rec) => rec.group_seq === removeRec.group_seq
          )
        );

        setRightCcGroupRcpt({ ...rightCcGroupRcpt, ...groupRightResult });
        setLeftCcGroupRcpt({ ...leftCcGroupRcpt, ...groupLeftResult });
      }
    }
  };

  const removeRecipientAction = (recipientType, typeSeq, removeRec) => {
    removeRecipient(recipientType, removeRec);
    moveTransferListData(recipientType, typeSeq, removeRec);
  };

  const viewFileAction = (fileCat, fileSeq) => {
    SetFilePreviewProperty({
      fileCategory: fileCat,
      fileSequence: fileSeq,
    });

    filePreviewDialogHandleOpen();
  };

  const removeFileConfirmAction = (fileRecord) => {
    confirmDialogHandleOpen({
      dialogTitle: "Circulation attachment file remove",
      dialogContent: "Are you sure to remove the attachment file.",
      confirmAction: () => removeFileAction(fileRecord),
      cancelAction: removeFileCancelAction,
    });
  };

  const removeFileCancelAction = () => {
    confirmDialogHandleClose();
  };

  const removeFileAction = (fileRecord) => {
    let result;
    let data = { ...circulationData };

    result = [...circulationData.messages.files].filter(
      (res) => res !== fileRecord
    );
    data.messages.files = result;

    setCirculationData({ ...circulationData, ...data });
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: { ...circulationData, ...data },
    });

    confirmDialogHandleClose();
  };

  const sendForwardCirculationConfirmAction = () => {
    if (validate()) {
      confirmDialogHandleOpen({
        dialogTitle: "Circulation Forward",
        dialogContent: "Are you sure to forward the circulation.",
        confirmAction: () => sendForwardCirculation(),
        cancelAction: sendNewCirculationCancelAction,
      });
    }
  };

  const sendNewCirculationCancelAction = () => {
    confirmDialogHandleClose();
  };

  const sendForwardCirculation = () => {
    confirmDialogHandleClose();
    setLockScreenLoadingOpen(true);
    createCirculation(circulationData).then((res) => {
      console.log(
        "*****************************************************************************"
      );
      console.log("System Module: Circulation");
      console.log("Message: Forward Circulation successful.");
      console.log(res);
      console.log(
        "*****************************************************************************"
      );

      setTimeout(() => {
        setLockScreenLoadingOpen(false);
        setRenewCircList(renewCircList + 1);
        resetCirculationData();
        closeCirculationDialog();
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "success",
          title: "Success Alert",
          content: "The Circulation have been forward",
        });
      }, 2000);
    });
  };

  const resetCirculationData = () => {
    setCirculationData(circulationDataTemplate);
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: null,
    });
  };

  // Dialog Button
  const recipientDialogBtn = [
    {
      buttonName: "Close",
      buttonFunction: selectRcptDialogHandleClose,
      chooseFile: false,
    },
  ];

  let circulationAttachmentPreviewDialogBtn = [];
  if (userModulesRedux !== null) {
    userModulesRedux.map((module) => {
      if (module.path === "/circulation") {
        if (module.rights.modifiable || module.rights.administrable) {
          circulationAttachmentPreviewDialogBtn = [
            {
              buttonName: "Annotation Save",
              buttonFunction: annotationSaveActionHandleOpen,
              chooseFile: false,
            },
          ];
        }
      }
    });
  }

  const validate = (fieldValues = circulationData) => {
    let temp = { ...validationErrors };

    if ("circ_subject" in fieldValues) {
      temp.circ_subject = fieldValues.circ_subject
        ? ""
        : "This field is required";
    }

    if ("TO" in fieldValues.recipients) {
      temp.TO =
        fieldValues.recipients.TO.length > 0 ? "" : "This field is required";
    }

    // if ("content" in fieldValues.messages) {
    // 	temp.content = fieldValues.messages.content ? "" : "This field is required"
    // }

    setValidationErrors({ ...temp });

    if (fieldValues == circulationData) {
      return Object.values(temp).every((x) => x == "");
    }
  };

  useEffect(() => {
    if (presetFiles !== null) {
      let data = { ...circulationData };

      data.messages.dms_property_seqs = propertySeqRedux;

      data.messages.files.push(...presetFiles);
      setCirculationData({ ...circulationData, ...data });
      dispatch({
        type: "CREATE_CIRCULATION_DATA",
        data: { ...circulationData, ...data },
      });
    }
  }, [presetFiles]);

  useEffect(() => {
    getUserGroupType().then((res) => {
      setUserGroupType(res.data);

      getUserListAction();
      getGroupListAction(res.data);
    });

    getForwardData(circulationSeq).then((res) => {
      setForwardDetail(res.data[0]);

      let forwardData = { ...circulationData };
      forwardData.circ_subject = res.data[0].subject;
      forwardData.forward_from_circ_seq = res.data[0].forward_from_circ_seq;
      forwardData.messages.content = res.data[0].contents;
      forwardData.messages.files = res.data[0].files;

      setCirculationSubject(res.data[0].subject);
      setEditorValue(res.data[0].contents);

      setCirculationData(forwardData);
      dispatch({
        type: "CREATE_CIRCULATION_DATA",
        data: forwardData,
      });
    });
  }, []);

  return (
    // console.log("Circulation Seqance | Variable: circulationSeq (CirculationForwardForm.js)"),
    // console.log(circulationSeq),

    // console.log("Circulation Forward Detail Data | Variable: forwardDetail (CirculationForwardForm.js)"),
    // console.log(forwardDetail),

    // console.log("User Group Type | Variable: userGroupType (CirculationForwardForm.js)"),
    // console.log(userGroupType),

    // console.log("User List | Variable: userList (CirculationForwardForm.js)"),
    // console.log(userList),

    // console.log("User List (Redux) | Variable: userListRedux (CirculationForwardForm.js)"),
    // console.log(userListRedux),

    // console.log("User Group List | Variable: userGroupList (CirculationForwardForm.js)"),
    // console.log(userGroupList),

    // console.log("User Group List (Redux) | Variable: userGroupListRedux (CirculationForwardForm.js)"),
    // console.log(userGroupListRedux),

    // console.log("Recipient Type | Variable: recipientType (CirculationForwardForm.js)"),
    // console.log(recipientType),

    // console.log("Circulation Data | Variable: circulationData (CirculationForwardForm.js)"),
    // console.log(circulationData),

    // console.log("Circulation Data (Redux) | Variable: circulationDataRedux (CirculationForwardForm.js)"),
    // console.log(circulationDataRedux),

    // console.log("Left To User Data | Variable: leftToUserRcpt (CirculationForwardForm.js)"),
    // console.log(leftToUserRcpt),

    // console.log("Right To User Data | Variable: rightToUserRcpt (CirculationForwardForm.js)"),
    // console.log(rightToUserRcpt),

    // console.log("Left CC User Data | Variable: leftCcUserRcpt (CirculationForwardForm.js)"),
    // console.log(leftCcUserRcpt),

    // console.log("Right CC User Data | Variable: rightCcUserRcpt (CirculationForwardForm.js)"),
    // console.log(rightCcUserRcpt),

    // console.log("Left To Group Data | Variable: leftToGroupRcpt (CirculationForwardForm.js)"),
    // console.log(leftToGroupRcpt),

    // console.log("Right To Group Data | Variable: rightToGroupRcpt (CirculationForwardForm.js)"),
    // console.log(rightToGroupRcpt),

    // console.log("Left Cc Group Data | Variable: leftCcGroupRcpt (CirculationForwardForm.js)"),
    // console.log(leftCcGroupRcpt),

    // console.log("Right Cc Group Data | Variable: rightCcGroupRcpt (CirculationForwardForm.js)"),
    // console.log(rightCcGroupRcpt),

    // console.log("CKEditor Value | Variable: editorValue (CirculationForwardForm.js)"),
    // console.log(editorValue),

    // console.log("---------------------------------------------------------------------------------"),

    <Fragment>
      <LockScreenLoading
        open={lockScreenLoadingOpen}
        circleSize={200}
        progressTitle={progressTitle}
      />
      <ConfirmDialog
        open={confirmDialogOpen}
        dialogTitle={confirmDialogData.dialogTitle}
        dialogContent={confirmDialogData.dialogContent}
        confirmAction={confirmDialogData.confirmAction}
        cancelAction={confirmDialogData.cancelAction}
      />

      <SaveWarnDialog
        open={saveWarnDialogOpen}
        dialogTitle={saveWarnDialogData.dialogTitle}
        dialogContent={saveWarnDialogData.dialogContent}
        saveAction={saveWarnDialogData.saveAction}
        dontSaveAction={saveWarnDialogData.dontSaveAction}
        cancelAction={saveWarnDialogData.cancelAction}
      />

      <FullScreenDialog
        open={filePreviewDialogOpen}
        dialogTitle={"Circuration Attachment File Preview"}
        formContent={<FilePreview filePreviewProperty={filePreviewProperty} />}
        button={circulationAttachmentPreviewDialogBtn}
        handleClose={filePreviewDialogHandleClose}
      />

      <NormalDialog
        open={selectUserDialogOpen}
        dialogTitle={`Select ${recipientType} Recipient`}
        formContent={
          <CirculationRecipientForm
            recipientType={recipientType}
            userGroupType={userGroupType}
            getUserListLoading={getUserListLoading}
            getGroupListLoading={getGroupListLoading}
            internalUserList={userList}
            externalUserList={externalUserList}
            userGroupList={userGroupList}
            reciptentChipAdd={reciptentChipAdd}
            reciptentChipRemove={reciptentChipRemove}
            leftToUserRcpt={leftToUserRcpt}
            setLeftToUserRcpt={setLeftToUserRcpt}
            rightToUserRcpt={rightToUserRcpt}
            setRightToUserRcpt={setRightToUserRcpt}
            leftToGroupRcpt={leftToGroupRcpt}
            setLeftToGroupRcpt={setLeftToGroupRcpt}
            rightToGroupRcpt={rightToGroupRcpt}
            setRightToGroupRcpt={setRightToGroupRcpt}
            leftCcUserRcpt={leftCcUserRcpt}
            setLeftCcUserRcpt={setLeftCcUserRcpt}
            rightCcUserRcpt={rightCcUserRcpt}
            setRightCcUserRcpt={setRightCcUserRcpt}
            leftCcGroupRcpt={leftCcGroupRcpt}
            setLeftCcGroupRcpt={setLeftCcGroupRcpt}
            rightCcGroupRcpt={rightCcGroupRcpt}
            setRightCcGroupRcpt={setRightCcGroupRcpt}
          />
        }
        // xs = extraSmall, sm = small, md = medium, lg = large, xl = extraLarge}
        size={"lg"}
        height={700}
        button={recipientDialogBtn}
        handleClose={selectRcptDialogHandleClose}
      />

      <div className={classes.root}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Legend
              id="to_recipient"
              label="TO"
              required={true}
              normalTitleColor={grayColor[10]}
              normalBorderColor={grayColor[4]}
              messageTitleColor={redColor[6]}
              messageBorderColor={redColor[6]}
              error={
                validationErrors.TO && validationErrors.TO !== "" ? true : false
              }
              helperText={validationErrors.TO}
            >
              <div className={classes.messageRecipientArea}>
                {circulationData.recipients.TO.map((res, index) => {
                  return (
                    <RecipientChip
                      key={index}
                      id={index}
                      rcptType={res.rcpt_type}
                      rcptName={
                        res.rcpt_type === "USER"
                          ? res.fullname
                          : res.rcpt_type === "USERGROUP"
                          ? res.group_name
                          : ""
                      }
                      removeAction={() =>
                        removeRecipientAction(
                          "TO",
                          res.rcpt_type === "USERGROUP" ? res.type_seq : "",
                          res
                        )
                      }
                    />
                  );
                })}
              </div>

              <Box className={classes.addFileBtnArea}>
                <Tooltip title="Add Recipient">
                  <Fab
                    color="primary"
                    size="small"
                    aria-label="add_recipient"
                    className={classes.addFileBtn}
                    onClick={() => selectRcptDialogHandleOpen("TO")}
                  >
                    <AddIcon />
                  </Fab>
                </Tooltip>
              </Box>
            </Legend>
          </Grid>

          <Grid item xs={12}>
            <Legend
              id="CC_recipient"
              label="CC"
              normalTitleColor={grayColor[10]}
              normalBorderColor={grayColor[4]}
              messageTitleColor={redColor[6]}
              messageBorderColor={redColor[6]}
            >
              <div className={classes.messageRecipientArea}>
                {circulationData.recipients.CC.map((res, index) => {
                  return (
                    <RecipientChip
                      key={index}
                      id={index}
                      rcptType={res.rcpt_type}
                      rcptName={
                        res.rcpt_type === "USER"
                          ? res.fullname
                          : res.rcpt_type === "USERGROUP"
                          ? res.group_name
                          : ""
                      }
                      removeAction={() =>
                        removeRecipientAction(
                          "CC",
                          res.rcpt_type === "USERGROUP" ? res.type_seq : "",
                          res
                        )
                      }
                    />
                  );
                })}
              </div>

              <Box className={classes.addFileBtnArea}>
                <Tooltip title="Add Recipient">
                  <Fab
                    color="primary"
                    size="small"
                    aria-label="add_recipient"
                    className={classes.addFileBtn}
                    onClick={() => selectRcptDialogHandleOpen("CC")}
                  >
                    <AddIcon />
                  </Fab>
                </Tooltip>
              </Box>
            </Legend>
          </Grid>

          <Grid item xs={12}>
            <Legend
              id="attachment"
              label="ATTACHMENT"
              normalTitleColor={grayColor[10]}
              normalBorderColor={grayColor[4]}
              messageTitleColor={redColor[6]}
              messageBorderColor={redColor[6]}
            >
              <Box className={classes.addFileBtnArea}>
                <FileDropzone
                  key={"circulation_Attachment"}
                  title={""}
                  fileType={"circulation_Attachment"}
                  disabled={false}
                  fileUploadAction={fileUploadAction}
                  fileRemoveAction={removeFileConfirmAction}
                  viewFileAction={viewFileAction}
                  DropzoneContent={CirculationFileZoneContent}
                  openFileManagerDialog={openFileManagerDialog}
                />
              </Box>
            </Legend>
          </Grid>

          <Grid item xs={12}>
            <TextField
              id={"circ_subject"}
              name={"circ_subject"}
              label={"Subject"}
              required
              type="text"
              fullWidth={true}
              size="small"
              variant="outlined"
              value={circulationSubject || ""}
              onChange={handleSubjectChange(
                validationErrors.circ_subject &&
                  validationErrors.circ_subject !== ""
                  ? true
                  : false
              )}
              error={
                validationErrors.circ_subject &&
                validationErrors.circ_subject !== ""
                  ? true
                  : false
              }
              helperText={validationErrors.circ_subject}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              border={
                validationErrors.content && validationErrors.content !== ""
                  ? 1
                  : 0
              }
              borderColor={
                validationErrors.content && validationErrors.content !== ""
                  ? "error.main"
                  : "grey.500"
              }
            >
              <CKEditor
                editor={ClassicEditor}
                data={editorValue}
                onChange={ckeditorHandleChange}
                config={editorConfiguration}
              />
            </Box>
            {validationErrors.content && validationErrors.content !== "" ? (
              <Typography
                variant="caption"
                color="error"
                className={classes.errorMsg}
              >
                {validationErrors.content}
              </Typography>
            ) : (
              ""
            )}
          </Grid>
        </Grid>
      </div>

      <div className={classes.btnArea}>
        <Button
          variant="contained"
          color="primary"
          onClick={sendForwardCirculationConfirmAction}
        >
          Send
        </Button>
      </div>
    </Fragment>
  );
};

export default CirculationCreateForm;
